import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';

/**
 * A voice turn's bubble content: a circular play/pause control, the clip's
 * own waveform, and its length — never a transcript (owner decision: no text
 * is ever derived from what was said, and no native browser audio chrome
 * either). It sits on the buyer's side of the thread on a neutral surface:
 * violet is Robbie's, and action blue belongs to the text bubble, so a voice
 * message is left to read as its own media object — but at full ink contrast,
 * because a washed-out control reads as broken rather than quiet.
 *
 * `peaks` are the real mic levels captured while recording (see
 * `useVoiceRecorder`), so the waveform is the shape of what was actually
 * said. Turns recorded before peaks were kept fall back to an even row —
 * an honest "no shape recorded", not invented decoration.
 *
 * `src` is undefined for a voice turn loaded from history (the chat API
 * deliberately omits clip bytes on that read) — the waveform and length
 * still render at full strength; only the play control goes quiet.
 */
export interface RobbieVoiceMessageProps {
  src?: string;
  /** Clip length in ms, measured while recording — see `useVoiceRecorder`. */
  durationMs?: number;
  /** Normalised 0..1 mic levels for the clip, oldest first. */
  peaks?: number[];
  className?: string;
}

const FALLBACK_BAR_COUNT = 40;
const BAR_MIN_PX = 2;
const BAR_MAX_PX = 24;
/** Bends the level curve so loud and quiet moments read apart at this size instead of averaging into a hedge. */
const BAR_CONTRAST = 1.4;

const barHeight = (peak: number): number => {
  const level = Math.pow(Math.min(1, Math.max(0, peak)), BAR_CONTRAST);
  return Math.round(BAR_MIN_PX + level * (BAR_MAX_PX - BAR_MIN_PX));
};

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** "12 seconds" / "1 minute 5 seconds" — for the play button's label, which is read aloud. */
const spokenLength = (seconds: number): string => {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  const minutes = m > 0 ? `${m} minute${m === 1 ? '' : 's'}` : '';
  const secs = s > 0 || m === 0 ? `${s} second${s === 1 ? '' : 's'}` : '';
  return [minutes, secs].filter(Boolean).join(' ');
};

export const RobbieVoiceMessage: React.FC<RobbieVoiceMessageProps> = ({
  src,
  durationMs,
  peaks,
  className = '',
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // The clip's length is whatever was measured while recording — NEVER
  // `audioElement.duration`. A MediaRecorder webm blob carries no duration
  // in its container, so Chromium reports `Infinity`, or some other bogus
  // finite value, until the file has been fully seeked through; reading it
  // here previously showed a duration that kept climbing instead of
  // settling. `durationMs` is the only source of truth for a voice turn's
  // length, so there is deliberately no code path that falls back to
  // `audioElement.duration` — nor to the recorder's cap.
  const duration = durationMs && durationMs > 0 ? durationMs / 1000 : 0;
  const elapsed = Math.min(currentTime, duration);
  const progress = duration > 0 ? elapsed / duration : 0;

  const bars = useMemo(
    () => (peaks && peaks.length > 0 ? peaks : Array<number>(FALLBACK_BAR_COUNT).fill(0.58)),
    [peaks]
  );

  const seekTo = useCallback(
    (ratio: number) => {
      const el = audioRef.current;
      if (!el || !duration) return;
      const next = Math.min(duration, Math.max(0, ratio * duration));
      el.currentTime = next;
      setCurrentTime(next);
    },
    [duration]
  );

  const seekFromPointer = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    seekTo((clientX - rect.left) / rect.width);
  };

  const onTrackKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = 1 / Math.max(duration, 1); // one second per press
    const moves: Record<string, number> = {
      ArrowRight: progress + step,
      ArrowUp: progress + step,
      ArrowLeft: progress - step,
      ArrowDown: progress - step,
      Home: 0,
      End: 1,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    seekTo(Math.min(1, Math.max(0, moves[event.key])));
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else el.play();
  };

  return (
    <div className={`flex w-full min-w-0 items-center gap-s3 ${className}`}>
      <button
        type="button"
        onClick={toggle}
        disabled={!src}
        aria-label={
          !src
            ? `Voice message, ${spokenLength(duration)} — not available to play back here`
            : playing
              ? 'Pause voice message'
              : `Play voice message, ${spokenLength(duration)}`
        }
        className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-round bg-ink text-white transition-colors
          duration-100 hover:bg-ink-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-action
          focus-visible:ring-offset-1 disabled:border disabled:border-line-strong disabled:bg-surface disabled:text-ink-muted"
      >
        {playing ? <Pause className="h-[13px] w-[13px] fill-current" /> : <Play className="h-[13px] w-[13px] translate-x-[1px] fill-current" />}
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div
          ref={trackRef}
          role="slider"
          tabIndex={src ? 0 : -1}
          aria-label="Voice message position"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(elapsed)}
          aria-valuetext={`${formatTime(elapsed)} of ${formatTime(duration)}`}
          aria-disabled={!src}
          onMouseDown={(e) => src && seekFromPointer(e.clientX)}
          onKeyDown={(e) => src && onTrackKeyDown(e)}
          className={`flex h-[26px] w-full min-w-0 items-center gap-[1.5px] rounded-control focus:outline-none
            focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1 ${src ? 'cursor-pointer' : ''}`}
        >
          {bars.map((peak, i) => {
            const played = src ? i / bars.length < progress : false;
            return (
              <span
                key={i}
                aria-hidden="true"
                className={`min-w-[2px] flex-1 rounded-round ${played ? 'bg-ink' : 'bg-ink-muted'}`}
                style={{ height: `${barHeight(peak)}px` }}
              />
            );
          })}
        </div>
        <span className="font-mono text-[10.5px] font-medium tabular-nums text-ink-secondary">
          {playing || elapsed > 0 ? formatTime(elapsed) : formatTime(duration)}
        </span>
      </div>
      {src && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- speech audio, playback driven by the custom control above
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setCurrentTime(0);
          }}
          className="hidden"
        />
      )}
    </div>
  );
};

export default RobbieVoiceMessage;
